import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, Flame } from "lucide-react";
import { LiveVisitorDot } from "@/components/LiveVisitorDot";
import { VisitorStatsModal } from "@/components/VisitorStatsModal";
import { useSiteStats } from "@/hooks/useSiteStats";

const formater = (n) => {
  const v = n || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1).replace(".", ",")}M`;
  if (v >= 10000) return `${(v / 1000).toFixed(1).replace(".", ",")}k`;
  return v.toLocaleString("fr-FR");
};

/** Compteur global de visiteurs du site.
 *  `variante` : "badge" (en-tête, en haut à droite) ou "minimal" (pied de page). */
export const VisitorCounter = ({ variante = "badge", suivre = true, clair = false }) => {
  const { stats, pointe, charge, chargerPointe } = useSiteStats({
    suivre,
    avecPointe: variante === "minimal",
  });
  const [ouvert, setOuvert] = useState(false);

  const basculer = () => {
    if (!ouvert) chargerPointe();
    setOuvert((o) => !o);
  };

  if (variante === "minimal") {
    return (
      <div className="flex flex-col items-center gap-2">
        <div
          data-testid="visitor-counter-minimal"
          className={`flex items-center gap-3 text-xs ${clair ? "text-stone-500" : "text-stone-400"}`}
        >
          <span className="flex items-center gap-1.5">
            <LiveVisitorDot taille="sm" />
            <span className={`font-semibold ${clair ? "text-emerald-600" : "text-emerald-400"}`}>
              {formater(stats.en_ligne?.nombre)}
            </span>
            en ligne
          </span>
          <span className={clair ? "text-stone-300" : "text-stone-600"}>•</span>
          <span>
            <span className={`font-semibold ${clair ? "text-stone-700" : "text-stone-300"}`}>
              {formater(stats.aujourdhui?.visiteurs_uniques)}
            </span>{" "}
            visiteurs aujourd'hui
          </span>
        </div>
        {pointe?.libelle && (
          <p
            data-testid="visitor-peak-hour"
            className={`flex items-center gap-1.5 text-xs ${clair ? "text-stone-500" : "text-stone-400"}`}
          >
            <Flame size={13} className="text-[#E05638]" />
            Le site est le plus animé entre{" "}
            <span className={`font-bold ${clair ? "text-[#E05638]" : "text-[#D4AF37]"}`}>
              {pointe.libelle}
            </span>{" "}
            — revenez à ce moment !
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <motion.button
        type="button"
        onClick={basculer}
        data-testid="visitor-counter-badge"
        aria-label="Voir les statistiques de fréquentation du site"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: charge ? 1 : 0.6, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="group flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md px-3.5 py-2 text-xs sm:text-sm text-stone-200 hover:bg-white/[0.12] hover:border-[#D4AF37]/50 transition-colors duration-200"
      >
        <span className="flex items-center gap-1.5">
          <LiveVisitorDot />
          <span className="font-bold text-emerald-400" data-testid="visitor-counter-online">
            {formater(stats.en_ligne?.nombre)}
          </span>
          <span className="text-stone-400">en ligne</span>
        </span>
        <span className="h-3.5 w-px bg-white/20" />
        <span className="flex items-center gap-1.5">
          <Eye size={14} className="text-[#D4AF37]" />
          <span className="font-bold text-stone-100" data-testid="visitor-counter-today">
            {formater(stats.aujourdhui?.visiteurs_uniques)}
          </span>
          <span className="hidden sm:inline text-stone-400">aujourd'hui</span>
        </span>
      </motion.button>

      <AnimatePresence>
        {ouvert && (
          <VisitorStatsModal stats={stats} pointe={pointe} onClose={() => setOuvert(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisitorCounter;
